import axios from "axios";
import { getEnvironmentInfo } from "../utils/environment";
import { safeStorage } from "../utils/storage";

// // 기본 baseURL 함수 (DEV/DEPLOY 선택)
const { base: BASE } = getEnvironmentInfo();
const API_BASE_URL = BASE;

// 토큰 갱신 중인지 확인하는 플래그
let isRefreshing = false;
let failedQueue = [];

// 실패한 요청들을 처리하는 함수
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// axios 응답 인터셉터 설정
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401 에러이고 아직 재시도하지 않은 요청인 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
      // 이미 토큰 갱신 중인 경우
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.token = token;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = safeStorage.getItem("refresh_token");

      if (!refreshToken) {
        // refresh_token이 없으면 로그아웃 처리
        handleLogout();
        return Promise.reject(error);
      }

      try {
        const response = await refreshTokenAPI(refreshToken);

        if (response.status === 200 && response.data?.token) {
          // 새로운 토큰들을 저장
          try {
            safeStorage.setItem("token", response.data.token);
            if (response.data.refresh_token) {
              safeStorage.setItem("refresh_token", response.data.refresh_token);
            }
          } catch (storageError) {
            console.error("토큰 갱신 저장 실패:", storageError);
            throw new Error("토큰 저장에 실패했습니다.");
          }

          // 대기 중인 요청들 처리
          processQueue(null, response.data.token);

          // 원래 요청 재시도
          originalRequest.headers.token = response.data.token;
          return axios(originalRequest);
        } else {
          // 토큰 갱신 실패
          throw new Error(response.data?.msg || "토큰 갱신 실패");
        }
      } catch (refreshError) {
        // 토큰 갱신 실패 시 로그아웃 처리
        processQueue(refreshError, null);
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// 로그아웃 처리 함수
const handleLogout = () => {
  // sessionStorage 정리
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("refresh_token");
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("userInfoLoaded");

  // 로그인 페이지로 리다이렉트
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// 쿠키로 토큰을 설정하는 함수 (필요시 사용)
// const setTokenCookie = (token) => {
//   if (token) {
//     document.cookie = `x-access-token=${token}; path=/; max-age=86400; SameSite=Lax`;
//   }
// };

// API 응답을 boolean으로 변환하는 헬퍼 함수
const convertResponseToString = (response) => {
  const msg = response.data?.msg || "";

  // 성공: msg에 "완료" 또는 "성공"이 포함되어 있으면 빈 문자열 반환
  if (msg.includes("완료") || msg.includes("성공")) {
    return "";
  }

  // 실패: msg 반환
  return msg;
};

// ====================== 헤더 ==================== //
// 이미지 변환
export const imageToImageUrl = async (data) => {
  const formData = new FormData();
  formData.append("file", data);

  try {
    const response = await axios.post(`${BASE}/image/getUrl`, formData, {
      headers: { token: sessionStorage.getItem("token") },
      withCredentials: true,
    });

    return response.data.imageUrl; // ✅ url → imageUrl로 변경
  } catch (error) {
    console.error("❌ 이미지 업로드 실패:", error);
    console.error("📦 에러 응답:", error.response?.data);
    throw error;
  }
};

// 로그인
export const loginAPI = (data) =>
  axios.post(`${BASE}/web/admin/sign-in/member`, data, {
    // withCredentials: true,
  });

// 로그아웃
export const logoutAPI = (actor, id) =>
  axios.post(
    `${BASE}/web/admin/sign-out`,
    {
      actor, // 'ADMIN' | 'MEMBER'
      id, // 대상 PK
    },
    {
      withCredentials: true,
    },
  );

// 자동로그인 토큰 재발급
export const refreshTokenAPI = async (refresh_token) => {
  try {
    const response = await axios.post(
      `${BASE}/web/admin/token/refresh`,
      { refresh_token },
      {
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 회원가입 (boolean 반환)
export const signupAPI = async (data) => {
  try {
    const response = await axios.post(`${BASE}/web/admin/create/member`, data, {
      withCredentials: true,
    });
    return convertResponseToString(response);
  } catch (error) {
    // 네트워크 오류나 서버 오류
    return error.response?.data?.msg || "서버 연결에 실패했습니다.";
  }
};

// 회원 조회 (중복 확인용)
export const getUserInfoAPI = async (username) => {
  try {
    const response = await axios.get(`${BASE}/web/admin/read/member`, {
      params: { username },
      withCredentials: true,
    });

    // total 값으로 중복 확인
    const total = response.data?.total || 0;

    if (total > 0) {
      // 중복된 아이디
      return "이미 사용 중인 아이디입니다.";
    } else {
      // 중복되지 않은 아이디
      return "";
    }
  } catch (error) {
    // 네트워크 오류나 서버 오류
    return error.response?.data?.msg || "중복 확인 중 오류가 발생했습니다.";
  }
};

// 사용자 상세 정보 조회 (로그인 후 사용)
export const getUserDetailAPI = async (username, role) => {
  try {
    const response = await axios.get(`${BASE}/web/admin/read/member`, {
      params: { username, role },
      withCredentials: true,
    });

    return response;
  } catch (error) {
    throw error;
  }
};

// 사용자 정보 업데이트
export const userUpdateAPI = (pwData) =>
  axios.post(`${API_BASE_URL}/user/update`, pwData, { withCredentials: true });

// 비밀번호 찾기
export const findPasswordAPI = async (data) => {
  try {
    const response = await axios.post(`${BASE}/web/admin/reset/member`, data, {
      withCredentials: true,
    });
    return convertResponseToString(response);
  } catch (error) {
    // 네트워크 오류나 서버 오류
    return error.response?.data?.msg || "서버 연결에 실패했습니다.";
  }
};

// 비밀번호 변경
export const changePasswordAPI = async (data) => {
  try {
    const response = await axios.post(`${BASE}/web/admin/reset/member`, data, {
      withCredentials: true,
    });
    return convertResponseToString(response);
  } catch (error) {
    // 네트워크 오류나 서버 오류
    return error.response?.data?.msg || "서버 연결에 실패했습니다.";
  }
};

// 아이디 찾기 — 바디: { name, phone, email } / 응답: { username }
export const findUsernameAPI = async (data) => {
  try {
    const response = await axios.post(`${BASE}/web/admin/find/member`, data, {
      withCredentials: true,
    });
    const body = response.data ?? {};
    const username = body.username ?? body.data?.username;
    if (username != null && String(username).trim() !== "") {
      return { ok: true, username: String(username).trim() };
    }
    const msg =
      body.msg ||
      (typeof body === "string" ? body : "") ||
      "일치하는 회원 정보를 찾을 수 없습니다.";
    return { ok: false, message: msg };
  } catch (error) {
    const msg =
      error.response?.data?.msg || error.message || "서버 연결에 실패했습니다.";
    return { ok: false, message: msg };
  }
};

// ======================================================== 단가 관련 ======================================================== //

// price_grade 조회
export const getPriceGradeAPI = async (priceGradeId) => {
  try {
    const response = await axios.get(`${BASE}/web/mission/settings`, {
      params: {
        table: "price_grade",
        id: priceGradeId,
      },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// price_grade_detail 조회
export const getPriceGradeDetailAPI = async (priceGradeId) => {
  try {
    const response = await axios.get(`${BASE}/web/mission/settings`, {
      params: {
        table: "price_grade_detail",
        price_grade_id: priceGradeId,
      },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 광고등록 관련
// 1. mission_service 조회
export const getMissionServiceAPI = async () => {
  try {
    const response = await axios.get(`${BASE}/web/mission/settings`, {
      params: { table: "mission_service" },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 2. mission_action 조회
export const getMissionActionAPI = async (key_id = null) => {
  try {
    const params = { table: "mission_action" };
    if (key_id !== null) {
      params.key_id = key_id;
    }

    const response = await axios.get(`${BASE}/web/mission/settings`, {
      params,
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 3. mission_template 조회
export const getMissionTemplateAPI = async (missionType, missionTemplate) => {
  try {
    const response = await axios.get(`${BASE}/web/mission/settings`, {
      params: { table: "mission_template", missionType, missionTemplate },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 3. ms_id 와 mt_id 로 과거주문 불러오기 missionType == ms_id missionTemplate == ma_id
export const getPastOrderAPI = async (
  webMemberId,
  missionType,
  missionTemplate,
  page = 1,
  limit = 200,
) => {
  try {
    const response = await axios.get(`${BASE}/web/mission/ad`, {
      params: {
        webMemberId,
        missionType,
        missionTemplate,
        status: "DONE",
        page,
        limit,
      },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 미션 등록
export const createMissionAPI = async (data) => {
  try {
    const response = await axios.post(`${BASE}/web/mission/ad_set_ad`, data, {
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 엑셀 대량등록
export const bulkCreateMissionAPI = async (rows, webMemberId) => {
  try {
    const response = await axios.post(
      `${BASE}/web/mission/bulk_upload`,
      { rows, webMemberId },
      { withCredentials: true },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 엑셀 대량등록 진행상황 조회
export const getBulkUploadStatusAPI = async (jobId) => {
  try {
    const response = await axios.get(
      `${BASE}/web/mission/bulk_upload/${jobId}/status`,
      { withCredentials: true },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 엑셀 대량등록 실패 행 조회 (실패 엑셀 재다운로드용)
export const getBulkUploadFailedItemsAPI = async (jobId) => {
  try {
    const response = await axios.get(
      `${BASE}/web/mission/bulk_upload/${jobId}/failed_items`,
      { withCredentials: true },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 엑셀 대량등록 내역(작업 목록) 조회
export const getBulkUploadJobsAPI = async (webMemberId) => {
  try {
    const response = await axios.get(`${BASE}/web/mission/bulk_upload`, {
      params: { webMemberId },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 광고 거절
export const rejectAdAPI = async (
  ad_id,
  webMemberId,
  adName,
  deposit,
  amount,
  status = "REJECTED",
  startDate = null,
  endDate = null,
) => {
  try {
    // status에 따라 title 결정
    let title = "";
    if (status === "DONE") {
      title = `광고 중단(${adName})`;
    } else {
      title = `등록 취소(${adName})`;
    }

    const requestBody = {
      ad_id,
      webMemberId,
      status,
      updateDeposit: {
        deposit,
        price: 0,
        logs: {
          balance: deposit,
          amount,
          admin_id: 0,
          title: title,
        },
      },
    };

    // 날짜가 제공된 경우에만 추가
    if (startDate) {
      requestBody.startDate = startDate;
    }
    if (endDate) {
      requestBody.endDate = endDate;
    }

    const response = await axios.patch(
      `${BASE}/web/mission/ad_set_ad`,
      requestBody,
      {
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== 알림 ======================================================== //
// 웹 회원 알림 조회
export const getWebMemberNotificationAPI = async (
  webMemberId,
  page = 1,
  pageSize = 10,
) => {
  try {
    const response = await axios.get(`${BASE}/web/notification/member`, {
      params: { webMemberId, page, pageSize },
      withCredentials: true,
      headers: { token: sessionStorage.getItem("token") },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 웹회원 알림 읽음 처리
export const readWebMemberNotificationAPI = async (wn_id, is_read = true) => {
  try {
    const response = await axios.patch(
      `${BASE}/web/notification/member`,
      {
        wn_id,
        is_read,
      },
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 어드민 알림 생성
export const createAdminNotificationAPI = async (
  toUserId,
  title,
  content,
  imageUrl,
  fromAdminId = 0,
) => {
  try {
    const response = await axios.post(
      `${BASE}/web/notification/admin`,
      {
        toUserId,
        title,
        content,
        imageUrl,
        fromAdminId,
      },
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== 파이어페이스 크롤링 관련 ======================================================== //

// 네이버 스마트스토어 크롤링 API
export const getNaverSmartStoreAPI = async (url) => {
  try {
    const response = await axios.get(
      // `https://crawlnaverstore-rjexfcaziq-du.a.run.app`,
      `${BASE}/web/mission/naver/product/parse-link`,
      {
        headers: { token: sessionStorage.getItem("token") },
        params: { url },
      },
      {
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 앱스토어 크롤링 API
export const getAppStoreAPI = async (playStoreUrl, appStoreUrl) => {
  try {
    const response = await axios.get(
      `https://crawlappstore-rjexfcaziq-uc.a.run.app`,
      {
        headers: { token: sessionStorage.getItem("token") },
        params: { playStoreUrl, appStoreUrl },
        // withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 네이버 플레이스 크롤링 API
export const getNaverPlayStoreAPI = async (url) => {
  try {
    const response = await axios.get(
      `https://us-central1-goldttak365-32898.cloudfunctions.net/crawlNaverPlace`,
      {
        headers: { token: sessionStorage.getItem("token") },
        params: { url },
        // withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 원부 미션 정보 받아오기
export const getOriginalInfoMissionAPI = async (productName, productId) => {
  try {
    const response = await axios.get(
      `https://us-central1-goldttak365-32898.cloudfunctions.net/crawlNaverSearchOriginProduct`,
      {
        headers: { token: sessionStorage.getItem("token") },
        params: { productName, productId },
        // withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 길찾기 정보 가져오기
export const getRouteInfoAPI = async (url) => {
  try {
    const response = await axios.get(
      `https://us-central1-goldttak365-32898.cloudfunctions.net/crawlNaverPlaceAroundWithQuiz`,
      {
        headers: { token: sessionStorage.getItem("token") },
        params: { url },
        // withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 네이버 쇼핑 리뷰 크롤링
export const getNaverShoppingReview = async (
  checkoutMerchantNo,
  originProductNo,
  productUrl,
  page = 1,
  pageSize = 20,
) => {
  try {
    // productUrl을 모바일 형식으로 변환
    let convertedProductUrl = productUrl;

    if (productUrl && productUrl.includes("smartstore.naver.com")) {
      // smartstore.naver.com을 m.smartstore.naver.com으로 변경
      convertedProductUrl = productUrl.replace(
        /https?:\/\/(www\.)?smartstore\.naver\.com/,
        "https://m.smartstore.naver.com",
      );

      // 쿼리 파라미터 제거 (물음표 이후 부분 제거)
      const urlWithoutQuery = convertedProductUrl.split("?")[0];
      convertedProductUrl = urlWithoutQuery;
    }

    const response = await axios.get(
      `https://asia-northeast3-goldttak365-32898.cloudfunctions.net/python-crawlnaverstorereviewdirect`,
      {
        headers: { token: sessionStorage.getItem("token") },
        params: {
          checkoutMerchantNo,
          originProductNo,
          productUrl: convertedProductUrl,
          page,
          pageSize,
        },
        // withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 쿠팡 상품정보 크롤링 API
export const getCoupangProductInfoAPI = async (productUrl) => {
  try {
    const response = await axios.get(
      `https://asia-northeast3-goldttak365-32898.cloudfunctions.net/coupangProductDetail`,
      {
        params: { productUrl },
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 쿠팡 상품 순위 크롤링 API
export const getCoupangProductRankAPI = async (keyword, productUrl) => {
  try {
    const response = await axios.get(
      `https://asia-northeast3-goldttak365-32898.cloudfunctions.net/coupangProductRank`,
      {
        params: { keyword, productUrl },
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== 미션(광고) 관련 ======================================================== //

// 회원의 광고 불러오기
export const getMemberAdAPI = async (
  webMemberId,
  page = 1,
  limit = 20,
  options = {},
) => {
  try {
    const { missionType, missionTemplate, checkerId, ...otherParams } = options;

    const params = {
      page,
      limit,
      ...otherParams,
    };

    // CHECKER 역할일 때는 checkerId 사용, 그 외에는 webMemberId 사용
    if (checkerId !== undefined) {
      params.checkerId = checkerId;
    } else if (webMemberId !== null && webMemberId !== undefined) {
      params.webMemberId = webMemberId;
    }

    // 옵션 파라미터들 (값이 있을 때만 추가)
    if (missionType !== undefined) {
      params.missionType = missionType;
    }

    if (missionTemplate !== undefined) {
      params.missionTemplate = missionTemplate;
    }

    const response = await axios.get(`${BASE}/web/mission/ad`, {
      params,
      withCredentials: true,
    });

    return response;
  } catch (error) {
    console.error("❌ getMemberAdAPI 에러:", error);
    throw error;
  }
};

// 전체 광고 불러오기
export const getAllMemberAdAPI = async (page = 1, limit = 20, options = {}) => {
  try {
    const { missionType, missionTemplate, ...otherParams } = options;

    const params = {
      page,
      limit,
      ...otherParams,
    };

    // 옵션 파라미터들 (값이 있을 때만 추가)
    if (missionType !== undefined) {
      params.missionType = missionType;
    }

    if (missionTemplate !== undefined) {
      params.missionTemplate = missionTemplate;
    }

    const response = await axios.get(`${BASE}/web/mission/ad`, {
      params,
      withCredentials: true,
    });

    return response;
  } catch (error) {
    console.error("❌ getMemberAdAPI 에러:", error);
    throw error;
  }
};

// 키워드 이름 조회
export const getKeywordNameAPI = async (
  adId = null,
  adIds = null,
  page = 1,
  limit = 100,
  type = null,
) => {
  try {
    const params = { page, limit };

    if (adIds && Array.isArray(adIds)) {
      // adIds 배열이 있으면 JSON 형태로 전송
      params.adIds = JSON.stringify(adIds);
    } else if (adId) {
      // 단일 adId가 있으면 ad_id로 전송
      params.ad_id = adId;
    }

    if (type) {
      params.type = type;
    }

    const response = await axios.get(`${BASE}/web/mission/keywords`, {
      params,
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 키워드 순위 업데이트
export const updateKeywordRankAPI = async (keywordId) => {
  try {
    const response = await axios.post(
      `${BASE}/web/mission/keywords/rank/update`,
      {
        keyword_id: keywordId,
        limit: 1000,
      },
    );

    return response;
  } catch (error) {
    throw error;
  }
};

// 쿠팡 키워드 순위 업데이트
export const updateCoupangKeywordRankAPI = async (keywordId) => {
  try {
    const response = await axios.post(
      `${BASE}/web/mission/keywords/rank/update/coupang`,
      {
        keyword_id: keywordId,
      },
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getKeywordRankHistoryAPI = async (keywordId) => {
  try {
    const response = await axios.get(`${BASE}/web/mission/keywords/rank`, {
      params: { keyword_id: keywordId },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 광고 ID로 키워드 순위 히스토리 조회
export const getAdKeywordRankHistoryAPI = async (adId) => {
  try {
    const response = await axios.get(`${BASE}/web/mission/keywords/rank`, {
      params: {
        ad_id: adId,
        limit: 100,
      },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 키워드 수정
export const updateKeywordAPI = async (keyword_id, name) => {
  try {
    const response = await axios.patch(
      `${BASE}/web/mission/keywords`,
      {
        keyword_id,
        name,
      },
      {
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 키워드 삭제
export const deleteKeywordAPI = async (keyword_id) => {
  try {
    const response = await axios.delete(
      `${BASE}/web/mission/keywords/${keyword_id}`,
      {
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 네이버 쇼핑 랭킹
export const getNaverShoppingRankAPI = async (data) => {
  try {
    const response = await axios.post(
      `${BASE}/web/mission/naver/rank/check/detail/1`,
      data,
      {
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 네이버 플레이스 순위
export const getNaverPlayStoreRankAPI = async (data) => {
  try {
    const response = await axios.post(
      `${BASE}/web/mission/naver/rank/check/detail/2`,
      data,
      {
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 광고 내역
export const getMissionHistoryAPI = async (ad_id) => {
  try {
    const response = await axios.get(`${BASE}/web/mission/ad/history`, {
      headers: { token: sessionStorage.getItem("token") },
      params: {
        ad_id,
      },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 미션 수량증가 받아오기
export const getMissionQuantityIncreaseAPI = async (params = {}) => {
  try {
    const response = await axios.get(
      `${BASE}/web/mission/daily-count-history`,
      {
        params,
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 미션 수량증가 생성
export const createMissionQuantityIncreaseAPI = async (data) => {
  try {
    const response = await axios.post(
      `${BASE}/web/mission/daily-count-history`,
      data,
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 미션 수량증가 수정
export const updateMissionQuantityIncreaseAPI = async (data) => {
  try {
    const response = await axios.patch(
      `${BASE}/web/mission/daily-count-history`,
      data,
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 미션 수량증가 삭제 (data: { id: number } 필수)
export const deleteMissionQuantityIncreaseAPI = async (data) => {
  try {
    const response = await axios.delete(
      `${BASE}/web/mission/daily-count-history`,
      {
        data,
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 미션 종료 받기
export const getMissionEndAPI = async (params = {}) => {
  try {
    const response = await axios.get(`${BASE}/web/mission/suspension-request`, {
      params,
      headers: { token: sessionStorage.getItem("token") },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 미션 종료 생성
export const createMissionEndAPI = async (data) => {
  try {
    const response = await axios.post(
      `${BASE}/web/mission/suspension-request`,
      data,
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 미션 종료 수정
export const updateMissionEndAPI = async (data) => {
  try {
    const response = await axios.patch(
      `${BASE}/web/mission/suspension-request`,
      data,
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 미션 종료 삭제
export const deleteMissionEndAPI = async (data) => {
  try {
    const response = await axios.delete(
      `${BASE}/web/mission/suspension-request`,
      data,
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== 예치금 관련 ======================================================== //

// 예치금 충전 생성
export const createDepositRequestAPI = async (data) => {
  try {
    const response = await axios.post(
      `${BASE}/web/deposit/create/deposit-req`,
      data,
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 예치금 이용내역 조회
export const getDepositLogAPI = async (params = {}) => {
  try {
    const response = await axios.get(`${BASE}/web/deposit/get/deposit-log`, {
      params: {
        page: params.page || 1,
        pageSize: params.pageSize || 20,
        ...params,
      },
      headers: { token: sessionStorage.getItem("token") },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 충전내역 조회
export const getChargeHistoryAPI = async (params = {}) => {
  try {
    const response = await axios.get(`${BASE}/web/deposit/get/deposit-req`, {
      params: {
        page: params.page || 1,
        pageSize: params.pageSize || 20,
        ...params,
      },
      headers: { token: sessionStorage.getItem("token") },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 충번내역 수정
export const patchChargeHistoryAPI = async (data) => {
  try {
    const response = await axios.patch(
      `${BASE}/web/deposit/patch/deposit-req`,
      data,
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 환불내역 조회
export const getRefundHistoryAPI = async (params = {}) => {
  try {
    const response = await axios.get(`${BASE}/web/deposit/get/refund-req`, {
      params: {
        page: params.page || 1,
        pageSize: params.pageSize || 20,
        ...params,
      },
      headers: { token: sessionStorage.getItem("token") },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 환불 요청 생성
export const createRefundRequestAPI = async (data) => {
  try {
    const response = await axios.post(
      `${BASE}/web/deposit/create/refund-req`,
      {
        web_member_id: data.web_member_id,
        amount: data.amount,
        contact: data.contact,
        user_memo: "환불 요청", // 고정값
        refund_bank: data.refund_bank,
        refund_bank_no: data.refund_bank_no,
        title: "환불 신청 선차감", // 고정값
        // admin_id는 비워놓음
      },
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 세금계산서 목록 조회
export const getTaxInvoiceListAPI = async (params = {}) => {
  try {
    const response = await axios.get(`${BASE}/web/deposit/get/tax-invoice`, {
      params: {
        page: params.page || 1,
        pageSize: params.pageSize || 20,
        ...params,
      },
      headers: { token: sessionStorage.getItem("token") },
      withCredentials: true,
    });

    return response;
  } catch (error) {
    console.error("세금계산서 목록 API 에러:", error);
    throw error;
  }
};

// 세금계산서 생성
export const createTaxInvoiceAPI = async (data) => {
  try {
    const response = await axios.post(
      `${BASE}/web/deposit/create/tax-invoice`,
      data,
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 세금계산서 수정
export const updateTaxInvoiceAPI = async (taxInvoiceId, data) => {
  try {
    const response = await axios.patch(
      `${BASE}/web/deposit/patch/tax-invoice`,
      {
        id: taxInvoiceId, // 수정할 세금계산서 ID
        webUserId: data.webUserId,
        name: data.name,
        phone: data.phone,
        email: data.email,
        businessRegistrationNumber: data.businessRegistrationNumber,
        companyName: data.companyName,
        companyPostCode: data.companyPostCode,
        companyAddress: data.companyAddress,
        companyExtraAddress: data.companyExtraAddress,
        businessType: data.businessType,
        businessIndustry: data.businessIndustry,
      },
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 광고 연장 API (PATCH) + 광고 키워드 추가
export const patchAdExtensionAPI = async (extensionData) => {
  try {
    const response = await axios.patch(
      `${BASE}/web/mission/ad_set_ad`,
      extensionData,
      {
        withCredentials: true,
      },
    );

    return response;
  } catch (error) {
    console.error("❌ patchAdExtensionAPI 실패:", error);
    throw error;
  }
};

// 어드민 전체 알림
export const sendAdminNotificationAPI = async (data) => {
  try {
    const response = await axios.post(`${BASE}/push/fcm/admin/all`, data, {
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 앱 유저 수 조회
export const getAppUserAPI = async () => {
  try {
    const response = await axios.get(`${BASE}/app/user/count`, {
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 웹 회원 조회
export const getWebUserAPI = async () => {
  try {
    const response = await axios.get(`${BASE}/web/admin/read/member`, {
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 웹회원 수정
export const updateWebUserAPI = async (data) => {
  try {
    const response = await axios.patch(`${BASE}/web/admin/patch/member`, data, {
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 체커 삭제
export const deleteCheckerAPI = async (checkerId) => {
  try {
    const response = await axios.delete(`${BASE}/web/admin/delete/member`, {
      data: { id: checkerId },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 체커 목록 조회
export const getCheckerListAPI = async (id) => {
  try {
    const response = await axios.get(`${BASE}/web/admin/read/member`, {
      params: { id },
      withCredentials: true,
    });

    return response;
  } catch (error) {
    throw error;
  }
};

// 체커 배정용 광고 목록 조회
export const getCheckerAssignedAdListAPI = async (
  webMemberId,
  page = 1,
  limit = 20,
  options = {},
) => {
  try {
    const params = {
      unassigned: true,
      webMemberId,
      page,
      limit,
      ...options,
    };

    // 필터 파라미터 추가
    if (options.missionName) {
      params.missionName = options.missionName;
    }
    if (options.storeName) {
      params.storeName = options.storeName;
    }

    const response = await axios.get(`${BASE}/web/mission/ad`, {
      params,
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 체커의 배정된 목록
export const getCheckerAdListAPI = async (checkerId) => {
  try {
    const response = await axios.get(`${BASE}/web/mission/ad`, {
      params: { checkerId },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 체커 광고 배정 또는 헤제
export const assignCheckerAdAPI = async (checkerId, adId) => {
  try {
    const response = await axios.patch(
      `${BASE}/web/mission/ad_set_ad`,
      {
        checkerId,
        ad_id: adId,
      },
      {
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== 순위 & 키워드 관련 ======================================================== //

// 연관 키워드 관련 키워드
export const getHotKeywordAPI = async (keywords) => {
  try {
    const response = await axios.get(`${BASE}/web/naver/keywords/tool`, {
      params: { keywords },
      withCredentials: true,
      headers: { token: sessionStorage.getItem("token") },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 키워드 블로그 발행수 확인
export const getKeywordBlogCountAPI = async (keywords) => {
  try {
    const response = await axios.get(
      `${BASE}/web/admin/naver/blog/publish-count`,
      {
        params: { keywords },
        withCredentials: true,
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 회원의 간편 상품 순위 결제 조회
export const getSimpleProductRankPaymentAPI = async () => {
  try {
    const response = await axios.get(
      `${BASE}/web/product-ranking/member-payments`,
      {
        withCredentials: true,
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 간편 광고 조회
export const getSimpleAdAPI = async () => {
  try {
    const response = await axios.get(`${BASE}/web/product-ranking/products`, {
      withCredentials: true,
      headers: { token: sessionStorage.getItem("token") },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// 간편 상품 등록 (본문 필드는 서버 스펙에 맞게 전달)
export const createSimpleProductAPI = async (payload) => {
  try {
    const response = await axios.post(
      `${BASE}/web/product-ranking/register`,
      payload,
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 간편 키워드 등록
export const createSimpleProductKeywordAPI = async (payload) => {
  try {
    const response = await axios.post(
      `${BASE}/web/product-ranking/products/add-keywords`,
      payload,
      {
        headers: { token: sessionStorage.getItem("token") },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 상품 삭제 (쿼리: id = 상품 id)
export const deleteSimpleProductAPI = async (productId) => {
  try {
    const response = await axios.delete(
      `${BASE}/web/product-ranking/products`,
      {
        params: { id: productId },
        withCredentials: true,
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 키워드 삭제 (쿼리: id = 키워드 id)
export const deleteSimpleProductKeywordAPI = async (keywordId) => {
  try {
    const response = await axios.delete(
      `${BASE}/web/product-ranking/keywords`,
      {
        params: { id: keywordId },
        withCredentials: true,
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 상품의 활성 키워드 전체 순위 수동 새로고침 (5분 쿨다운)
export const refreshSimpleProductRankAPI = async (productId) => {
  try {
    const response = await axios.post(
      `${BASE}/web/product-ranking/products/refresh`,
      { id: productId },
      {
        withCredentials: true,
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 키워드 히스토리 조회 (keyword_id = 키워드 id)
export const getSimpleProductKeywordHistoryAPI = async (keywordId) => {
  try {
    const response = await axios.get(
      `${BASE}/web/product-ranking/keyword-histories`,
      {
        params: { keyword_id: keywordId },
        withCredentials: true,
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 간단 상품 결제 정보 조회
export const getSimpleGetProductRankPaymentAPI = async () => {
  try {
    const response = await axios.get(
      `${BASE}/web/product-ranking/payment-settings`,
      {
        withCredentials: true,
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 간단 상품 결제
export const updateSimpleProductRankPaymentAPI = async (payload) => {
  try {
    const response = await axios.post(
      `${BASE}/web/product-ranking/member-payments`,
      payload,
      {
        withCredentials: true,
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// 간단 상품 구독 취소
export const cancelSimpleProductRankPaymentAPI = async (payload) => {
  try {
    const response = await axios.patch(
      `${BASE}/web/product-ranking/member-payments`,
      payload,
      {
        withCredentials: true,
        headers: { token: sessionStorage.getItem("token") },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};
